import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Callout } from '@/shared/ui/Callout';
import { CopyButton } from '@/shared/ui/CopyButton';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Table, type Column } from '@/shared/ui/Table';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/Toast';
import { errorMessage } from '@/modules/reviews/sms/hooks';
import {
  MAX_TRACKED_LINKS,
  trackedLinksApi,
  type ResetClicksInput,
  type TrackedLink,
  type TrackedLinkClick,
  type TrackedLinkInput,
} from '../../api/trackedLinksApi';
import { agencyKeys } from '../../queryKeys';
import styles from './TrackedLinksPanel.module.css';

const SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;

function slugError(slug: string): string | undefined {
  if (!slug) return undefined;
  if (slug.length < 3) return 'At least 3 characters';
  if (slug.length > 40) return 'At most 40 characters';
  if (!SLUG_RE.test(slug)) return 'Letters, digits, - and _ only';
  return undefined;
}

function urlError(url: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return 'Only http(s) links';
    return undefined;
  } catch {
    return 'Enter a full URL, starting with https://';
  }
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDay(day: string): string {
  // "2026-10-25" is a UTC bucket; show it as that day, not shifted into the viewer's zone.
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** A datetime-local value ("2026-10-25T14:30") read in the viewer's zone, as ISO. */
function localToIso(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

// ─── Link form (create + edit) ────────────────────────────────────────────────

function LinkForm({
  baseUrl,
  initial,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: {
  baseUrl: string;
  initial?: TrackedLink;
  submitLabel: string;
  busy: boolean;
  onSubmit: (input: TrackedLinkInput) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [targetUrl, setTargetUrl] = useState(initial?.targetUrl ?? '');

  const normalisedSlug = slug.trim().toLowerCase();
  const slugErr = slugError(normalisedSlug);
  const urlErr = urlError(targetUrl.trim());
  const canSubmit = name.trim().length > 0 && targetUrl.trim().length > 0 && !slugErr && !urlErr;

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({
          name: name.trim(),
          targetUrl: targetUrl.trim(),
          ...(normalisedSlug ? { slug: normalisedSlug } : {}),
        });
      }}
    >
      <div className={styles.grid2}>
        <Input
          label="Label"
          placeholder="Online booking"
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
        <Input
          label="Link name"
          placeholder={initial ? initial.slug : 'book (leave blank for a random one)'}
          value={slug}
          maxLength={40}
          onChange={(e) => setSlug(e.target.value)}
          error={slugErr}
          hint={`${baseUrl}/${normalisedSlug || (initial ? initial.slug : '…')}`}
          fullWidth
        />
      </div>
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor={`target-${initial?.id ?? 'new'}`}>
          Long URL
        </label>
        <textarea
          id={`target-${initial?.id ?? 'new'}`}
          className={styles.textarea}
          rows={3}
          placeholder="https://providers.example.com/book/123?tt=…"
          value={targetUrl}
          onChange={(e) => setTargetUrl(e.target.value)}
        />
        {urlErr && <p className={styles.hint}>{urlErr}</p>}
      </div>
      <div className={styles.actions}>
        {onCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Button type="submit" size="sm" loading={busy} disabled={!canSubmit || busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

// ─── Clicks ───────────────────────────────────────────────────────────────────

function ClicksSection({ practiceId, link }: { practiceId: string; link: TrackedLink }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [confirm, setConfirm] = useState<ResetClicksInput | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: agencyKeys.trackedLinkClicks(practiceId, link.id),
    queryFn: () => trackedLinksApi.clicks(practiceId, link.id),
  });

  const reset = useMutation({
    mutationFn: (input: ResetClicksInput) =>
      trackedLinksApi.resetClicks(practiceId, link.id, input),
    onSuccess: (result) => {
      toast.success(
        result.deleted === 0
          ? 'No clicks matched.'
          : `Cleared ${result.deleted} click${result.deleted === 1 ? '' : 's'}; ${result.remaining} left.`,
      );
      setSelected(new Set());
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: agencyKeys.trackedLinkClicks(practiceId, link.id) });
      void qc.invalidateQueries({ queryKey: agencyKeys.trackedLinks(practiceId) });
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const clicks = data?.clicks ?? [];
  const allSelected = clicks.length > 0 && clicks.every((c) => selected.has(c.id));

  const columns = useMemo<Column<TrackedLinkClick>[]>(
    () => [
      {
        key: 'select',
        header: '',
        width: '32px',
        render: (row) => (
          <input
            type="checkbox"
            className={styles.checkbox}
            aria-label="Select click"
            checked={selected.has(row.id)}
            onChange={(e) => {
              const next = new Set(selected);
              if (e.target.checked) next.add(row.id);
              else next.delete(row.id);
              setSelected(next);
            }}
          />
        ),
      },
      { key: 'when', header: 'When', render: (row) => formatWhen(row.clickedAt) },
      {
        key: 'ip',
        header: 'IP',
        render: (row) => <span className={styles.mono}>{row.ip ?? '—'}</span>,
      },
      {
        key: 'ua',
        header: 'Browser',
        render: (row) => (
          <span className={clsx(styles.truncate, styles.muted)} title={row.userAgent ?? undefined}>
            {row.userAgent ?? '—'}
          </span>
        ),
      },
    ],
    [selected],
  );

  const rangeInput: ResetClicksInput | null = useMemo(() => {
    const f = localToIso(from);
    const t = localToIso(to);
    if (!f && !t) return null;
    if (f && t && f > t) return null;
    return { mode: 'range', ...(f ? { from: f } : {}), ...(t ? { to: t } : {}) };
  }, [from, to]);

  const confirmCopy = (input: ResetClicksInput): { title: string; message: string } => {
    switch (input.mode) {
      case 'all':
        return {
          title: 'Reset click count?',
          message: `Every recorded click on "${link.name}" is deleted and the count starts again at 0. This cannot be undone.`,
        };
      case 'ids':
        return {
          title: `Delete ${input.ids.length} selected click${input.ids.length === 1 ? '' : 's'}?`,
          message: 'Only the chosen rows go. This cannot be undone.',
        };
      case 'range':
        return {
          title: 'Delete clicks in this window?',
          message: `Every click ${input.from ? `from ${formatWhen(input.from)}` : 'before'}${
            input.from && input.to ? ' to ' : ' '
          }${input.to ? formatWhen(input.to) : ''}${
            input.from && !input.to ? ' onward' : ''
          } is deleted. This cannot be undone.`,
        };
    }
  };

  return (
    <div className={styles.clicks}>
      <div className={styles.clicksHeader}>
        <h4 className={styles.sectionTitle}>
          Clicks{' '}
          <span className={styles.muted}>
            {data ? `· ${data.total} total` : ''}
            {data && data.total > data.clicks.length
              ? ` (showing latest ${data.clicks.length})`
              : ''}
          </span>
        </h4>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={!data || data.total === 0}
          onClick={() => setConfirm({ mode: 'all' })}
        >
          Reset all
        </Button>
      </div>

      {isLoading && <Skeleton height={120} borderRadius="var(--radius-md)" />}

      {data && data.total === 0 && <div className={styles.empty}>No clicks yet.</div>}

      {data && data.total > 0 && (
        <>
          <div className={styles.summaries}>
            <div className={styles.summary}>
              <h5 className={styles.sectionTitle}>By date (UTC)</h5>
              <ul className={styles.summaryList}>
                {data.byDay.map((d) => (
                  <li key={d.day} className={styles.summaryRow}>
                    <span className={styles.summaryKey}>{formatDay(d.day)}</span>
                    <span className={styles.summaryValue}>{d.count}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.summary}>
              <h5 className={styles.sectionTitle}>By IP</h5>
              <ul className={styles.summaryList}>
                {data.byIp.map((row) => (
                  <li key={row.ip} className={styles.summaryRow}>
                    <span
                      className={styles.summaryKey}
                      title={`Last ${formatWhen(row.lastClickAt)}`}
                    >
                      {row.ip}
                    </span>
                    <span className={styles.summaryValue}>{row.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className={styles.toolbar}>
            <label className={styles.rangeField}>
              Delete from
              <input
                type="datetime-local"
                className={styles.rangeInput}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className={styles.rangeField}>
              to
              <input
                type="datetime-local"
                className={styles.rangeInput}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!rangeInput}
              onClick={() => rangeInput && setConfirm(rangeInput)}
            >
              Delete in range
            </Button>
          </div>

          <div className={styles.actionsBetween}>
            <label className={styles.rangeField}>
              <span>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={allSelected}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(clicks.map((c) => c.id)) : new Set())
                  }
                />{' '}
                Select all shown
              </span>
            </label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={selected.size === 0}
              onClick={() => setConfirm({ mode: 'ids', ids: [...selected] })}
            >
              Delete selected{selected.size > 0 ? ` (${selected.size})` : ''}
            </Button>
          </div>

          <Table columns={columns} data={clicks} emptyMessage="No clicks yet." />
        </>
      )}

      {confirm && (
        <ConfirmDialog
          isOpen
          onClose={() => setConfirm(null)}
          onConfirm={() => reset.mutate(confirm)}
          variant="destructive"
          confirmLabel="Delete"
          isLoading={reset.isPending}
          {...confirmCopy(confirm)}
        />
      )}
    </div>
  );
}

// ─── One link ─────────────────────────────────────────────────────────────────

function LinkCard({
  practiceId,
  baseUrl,
  link,
  open,
  onToggle,
}: {
  practiceId: string;
  baseUrl: string;
  link: TrackedLink;
  open: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: agencyKeys.trackedLinks(practiceId) });

  const update = useMutation({
    mutationFn: (input: TrackedLinkInput) => trackedLinksApi.update(practiceId, link.id, input),
    onSuccess: () => {
      toast.success('Link updated.');
      setEditing(false);
      void invalidate();
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: () => trackedLinksApi.remove(practiceId, link.id),
    onSuccess: () => {
      toast.success('Link deleted.');
      setConfirmDelete(false);
      void invalidate();
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  return (
    <section className={clsx(styles.link, open && styles.linkOpen)}>
      <div className={styles.linkHeader}>
        <div className={styles.linkMeta}>
          <h3 className={styles.linkTitle}>{link.name}</h3>
          <div className={styles.shortRow}>
            <a
              className={styles.shortUrl}
              href={link.shortUrl}
              target="_blank"
              rel="noreferrer"
              title="Opens the short link in a new tab; this counts as a click"
            >
              {link.shortUrl}
            </a>
            <CopyButton value={link.shortUrl} label={`Copy ${link.name} short link`} />
          </div>
          <p className={styles.target} title={link.targetUrl}>
            →{' '}
            <a href={link.targetUrl} target="_blank" rel="noreferrer">
              {link.targetUrl}
            </a>
          </p>
        </div>
        <div className={styles.linkActions}>
          <span className={link.clickCount > 0 ? styles.count : styles.countZero}>
            {link.clickCount} click{link.clickCount === 1 ? '' : 's'}
          </span>
          <Button type="button" variant={open ? 'secondary' : 'ghost'} size="sm" onClick={onToggle}>
            {open ? 'Hide clicks' : 'Clicks'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setEditing((v) => !v)}
            disabled={update.isPending}
          >
            {editing ? 'Cancel' : 'Edit'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setConfirmDelete(true)}
            disabled={remove.isPending}
          >
            Delete
          </Button>
        </div>
      </div>

      {link.lastClickAt && <p className={styles.hint}>Last click {formatWhen(link.lastClickAt)}</p>}

      {editing && (
        <LinkForm
          baseUrl={baseUrl}
          initial={link}
          submitLabel="Save"
          busy={update.isPending}
          onSubmit={(input) => update.mutate(input)}
          onCancel={() => setEditing(false)}
        />
      )}

      {open && <ClicksSection practiceId={practiceId} link={link} />}

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        title={`Delete "${link.name}"?`}
        message="The short link stops working straight away and its clicks are deleted with it. Anything already printed or sent with this link will land on a 'Link not found' page."
        confirmLabel="Delete link"
        variant="destructive"
        isLoading={remove.isPending}
      />
    </section>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

/**
 * Command Center → a practice → Settings → Tracked Links (agency admins only; nothing of this
 * shows in the practice's own view).
 *
 * Up to five short URLs per practice for the long links onboarding throws up — an online-booking
 * deep link, say. Every visit to a short link is recorded with its time and IP; the admin can
 * reset the count outright, delete chosen clicks, or delete a date/time window.
 */
export function TrackedLinksPanel({ practiceId }: { practiceId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: agencyKeys.trackedLinks(practiceId),
    queryFn: () => trackedLinksApi.list(practiceId),
  });

  const create = useMutation({
    mutationFn: (input: TrackedLinkInput) => trackedLinksApi.create(practiceId, input),
    onSuccess: (link) => {
      toast.success(`Short link ready: ${link.shortUrl}`);
      setCreating(false);
      void qc.invalidateQueries({ queryKey: agencyKeys.trackedLinks(practiceId) });
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const links = data?.links ?? [];
  const atCap = links.length >= MAX_TRACKED_LINKS;

  return (
    <Card
      title="Tracked links"
      subtitle={`Up to ${MAX_TRACKED_LINKS} short URLs for this practice. Each click is recorded with its date and IP.`}
      action={
        data && !atCap && !creating ? (
          <Button type="button" size="sm" onClick={() => setCreating(true)}>
            New short link
          </Button>
        ) : undefined
      }
    >
      <div className={styles.stack}>
        <p className={styles.intro}>
          For the long links onboarding throws up — an online-booking page with a screen of query
          string, say. Paste the long URL, pick a link name or take a random one, and hand out the
          short URL. Only PracticeNova admins see this; the practice does not.
        </p>

        {isLoading && <Skeleton height={160} borderRadius="var(--card-radius)" />}
        {isError && (
          <Callout variant="error" title="Could not load tracked links">
            Reload the page to try again.
          </Callout>
        )}

        {data && creating && (
          <section className={styles.link}>
            <h3 className={styles.linkTitle}>New short link</h3>
            <LinkForm
              baseUrl={data.baseUrl}
              submitLabel="Create short link"
              busy={create.isPending}
              onSubmit={(input) => create.mutate(input)}
              onCancel={() => setCreating(false)}
            />
          </section>
        )}

        {data && links.length === 0 && !creating && (
          <div className={styles.empty}>No tracked links yet.</div>
        )}

        {links.map((link) => (
          <LinkCard
            key={link.id}
            practiceId={practiceId}
            baseUrl={data?.baseUrl ?? ''}
            link={link}
            open={openId === link.id}
            onToggle={() => setOpenId((cur) => (cur === link.id ? null : link.id))}
          />
        ))}

        {data && atCap && (
          <p className={styles.hint}>
            This practice has all {MAX_TRACKED_LINKS} links. Delete one to add another.
          </p>
        )}
      </div>
    </Card>
  );
}
